import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TextInput, Button } from '@pwc/pwc-deals-ui';
import ClearableDisplayPill from '../Pills/ClearableDisplayPill';
import OutsideClickHandler from '../OutsideClickHandler';
import {
  findMatches,
  applySelectedMatches,
  isContainsMatchBuilder,
} from './selectMultipleHelpers';
import './SelectMultiple.scss';

export {
  isContainsMatchBuilder,
  isStartsWithMatchBuilder,
} from './selectMultipleHelpers';

const SelectMultiple = (props) => {
  const {
    fieldName,
    label,
    placeholder = '',
    options,
    selections = [],
    onChange, // Note: parameter is an an array of updated selections (not event)
    onBlur = () => {},
    error = '',
    isMatchBuilder = isContainsMatchBuilder, // Note: searchText => optionLabel => isMatch
    minimumSearchStringLength = 1,
    noMatchElement = null, // Note: could be used for a "Create Tag"
    disabled,
  } = props;

  const [searchText, setSearchText] = useState('');
  const [matches, setMatches] = useState(null);
  const [shouldShowApply, setShouldShowApply] = useState(false);

  const resetSearch = () => {
    setMatches(null);
    setSearchText('');
    setShouldShowApply(false);
  };

  const onBlurSearchText = () => {
    if (matches === null) {
      setSearchText('');
      onBlur();
    } else {
      // Not a "real" blur. The blur event on the search box was due to a user clicking in the displayed matches
    }
  };

  const onSearchTextChange = (event) => {
    const updatedSearchText = event.target.value;

    setMatches(
      updatedSearchText.length < minimumSearchStringLength
        ? null
        : findMatches(updatedSearchText, options, selections, isMatchBuilder)
    );

    setSearchText(updatedSearchText);
  };

  const onClick = (option) => {
    const updatedMatches = matches.map((match) => {
      const updatedMatch = { ...match };
      if (match.value === option.value) {
        updatedMatch.selected = !updatedMatch.selected;
      }
      return updatedMatch;
    });
    setMatches(updatedMatches);
    setShouldShowApply(updatedMatches.some((x) => x.selected));
  };

  const onApplyClick = () => {
    const updatedSelections = applySelectedMatches(selections, matches);
    onChange(updatedSelections);
    resetSearch();
  };

  const onSelectionRemoved = (option) => {
    const updatedSelections = selections.filter(
      (selection) => selection.value !== option.value
    );

    onChange(updatedSelections);
    resetSearch();
  };

  const onOutsideClick = () => {
    resetSearch();
  };

  const pills =
    selections &&
    selections.length > 0 &&
    selections.map((o = {}, i) => {
      return (
        o && (
          <ClearableDisplayPill
            onClearClick={() => onSelectionRemoved(o)}
            tag={o.label}
            index={i}
            key={o.value}
          />
        )
      );
    });

  const displayedOptions =
    matches &&
    matches.map((m) => (
      <div key={m.label}>
        <input
          className="item-checkbox"
          id={m.label}
          data-testid={m.label}
          type="checkbox"
          name={m.label}
          checked={m.selected || false}
          onChange={() => {
            onClick(m);
          }}
        />
        <label htmlFor={m.label}>{m.label}</label>
      </div>
    ));

  return (
    <div className="select-multiple">
      <TextInput
        basic
        label={label}
        field={{
          name: fieldName,
          value: searchText,
          onChange: onSearchTextChange,
        }}
        autoComplete="off"
        error={matches !== null ? '' : error}
        onBlur={onBlurSearchText}
        placeholder={placeholder}
        iconLeft="search"
        disabled={disabled}
      />
      {matches && matches.length > 0 && (
        <OutsideClickHandler onClick={onOutsideClick}>
          <div>
            <div className="input-field-container">
              <div
                className="multi-select-container"
                data-testid="multi-select-container"
              >
                <div className="scrollable">{displayedOptions}</div>
                {shouldShowApply && (
                  <div className="action-button-container">
                    <Button
                      data-testid="apply-selections-button"
                      onClick={onApplyClick}
                      type="button"
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </OutsideClickHandler>
      )}
      {matches && matches.length === 0 && noMatchElement != null && (
        <div>{noMatchElement}</div>
      )}

      {pills && <div className="pills">{pills}</div>}
    </div>
  );
};

SelectMultiple.propTypes = {
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.exact({
      value: PropTypes.any,
      label: PropTypes.string,
    })
  ).isRequired,
  selections: PropTypes.arrayOf(
    PropTypes.exact({
      value: PropTypes.any,
      label: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  isMatchBuilder: PropTypes.func,
  minimumSearchStringLength: PropTypes.number,
  noMatchElement: PropTypes.element,
};

SelectMultiple.defaultProps = {
  placeholder: '',
  onBlur: () => {},
  error: '',
  isMatchBuilder: isContainsMatchBuilder,
  minimumSearchStringLength: 1,
  noMatchElement: null,
};

export default SelectMultiple;
